import { EAuthenticationType } from "@kuika/kuika-cl-model";
import * as React from "react";
import { InputTooltip } from "../../../common/input-tooltip";
import { KuikaFormField } from "../../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  editGridNavId,
  focusEditGridCell,
  IKuikaEditGridColumn,
  KuikaEditGrid,
  KuikaEditGridCell,
  KuikaEditGridRemove,
  KuikaEditGridRow
} from "../../shared/kuika-edit-grid/kuika-edit-grid";
import {
  IEditGridListApi,
  IEditGridListRow,
  KuikaEditGridFormList
} from "../../shared/kuika-edit-grid/kuika-edit-grid-form-list";
import {
  blankHeaderParameter,
  describeGeneratedHeaderSource,
  IApiKeyParameter,
  IGeneratedHeaderRowIndexes,
  isBlankHeaderParameter,
  isGeneratedHeaderRow,
  PARAMETER_TYPE_AUTH_TOKEN,
  PARAMETER_TYPE_BASIC_AUTH,
  PARAMETER_TYPE_CUSTOM,
  PARAMETER_TYPE_USERNAME
} from "./rest-connection-headers";

/**
 * The credential fields an auth type needs, plus the header table every type carries. Shared
 * by the API connection forms; the rules the table enforces live in `rest-connection-headers`.
 *
 * Replaces the antd-based authorization input group these forms used to share. The shell
 * drives `rc-field-form` directly, and the two engines do not share a store, so a form on
 * `KuikaFormPage` cannot host antd `Form.Item`s at all.
 */
export interface IRestConnectionAuthSectionProps {
  authType: EAuthenticationType;
  /** Which row already holds each generated header; -1 when none does. */
  userNameHeaderParameterIndex: number;
  authTokenHeaderParameterIndex: number;
  basicAuthHeaderParameterIndex: number;
  /** Where "Learn more" goes — each datasource type has its own guide. */
  helpLink: string;
  /** Prefixes the derived test ids, e.g. `odata` → `odataHeaderDraftKeyInput`. */
  testIdPrefix: string;
}

interface IAuthFieldSpec {
  name: string;
  label: string;
  placeholder: string;
  description: string;
  isPassword?: boolean;
}

const CLIENT_FIELDS: IAuthFieldSpec[] = [
  {
    name: "ClientIdKeyName",
    label: "ID key name",
    placeholder: "Enter client ID key name",
    description: "Enter the name of the unique key that identifies the client."
  },
  {
    name: "ClientId",
    label: "ID",
    placeholder: "Enter client ID",
    description: "Enter the Client ID information provided by the provider of the API."
  },
  {
    name: "ClientSecretKeyName",
    label: "Secret key name",
    placeholder: "Enter client secret key name",
    description: "Enter the name of the secret key that identifies the client."
  },
  {
    name: "ClientSecret",
    label: "Secret",
    placeholder: "Enter client secret",
    description: "Enter the Client Secret information provided by the provider of the API.",
    isPassword: true
  }
];

const BASIC_FIELDS: IAuthFieldSpec[] = [
  {
    name: "BasicAuthUsername",
    label: "Username",
    placeholder: "Enter basic auth username",
    description: "Enter the username required to connect with Basic Authentication."
  },
  {
    name: "BasicAuthPassword",
    label: "Password",
    placeholder: "Enter basic auth password",
    description: "Enter the password required to connect with Basic Authentication.",
    isPassword: true
  }
];

/** Where the header rows live in the form's store — the same path in every API form. */
const HEADER_ROWS_PATH: string[] = ["extraHeaderParameters"];

const HEADER_COLUMNS: IKuikaEditGridColumn[] = [
  // Wide enough for the longest generated type ("Basic Auth.", "Auth. Token") beside the
  // select's chevron: at 124px the pinned Basic row read "Basic A…".
  { id: "type", header: "Type", track: "148px" },
  { id: "key", header: "Key", track: "minmax(0, 1.1fr)" },
  { id: "value", header: "Value", track: "minmax(0, 1fr)" },
  { id: "remove", track: "52px" }
];

const TYPE_LABELS: Record<string, string> = {
  [PARAMETER_TYPE_CUSTOM]: "Custom",
  [PARAMETER_TYPE_USERNAME]: "Username",
  [PARAMETER_TYPE_BASIC_AUTH]: "Basic Auth.",
  [PARAMETER_TYPE_AUTH_TOKEN]: "Auth. Token"
};

export const RestConnectionAuthSection = ({
  authType,
  userNameHeaderParameterIndex,
  authTokenHeaderParameterIndex,
  basicAuthHeaderParameterIndex,
  helpLink,
  testIdPrefix
}: IRestConnectionAuthSectionProps): React.ReactElement => {
  const [revealedField, setRevealedField] = React.useState<string | null>(null);
  const generatedRows: IGeneratedHeaderRowIndexes = {
    userNameHeaderParameterIndex,
    authTokenHeaderParameterIndex,
    basicAuthHeaderParameterIndex
  };
  const lockedValueTooltip: string = describeGeneratedHeaderSource(authType);

  const renderCredentialField = (field: IAuthFieldSpec): React.ReactNode => {
    const isRevealed: boolean = revealedField === field.name;

    return (
      <KuikaFormField
        key={field.name}
        name={field.name}
        label={field.label}
        labelSuffix={<InputTooltip description={field.description} helpLink={helpLink} />}
      >
        <KuikaInput
          size="md"
          placeholder={field.placeholder}
          data-testid={`${testIdPrefix}${field.name}Input`}
          type={field.isPassword && !isRevealed ? "password" : "text"}
          // The API's credentials, never the signed-in user's: without these the browser fills
          // its saved Kuika login into Username/Password and into Secret key name/Secret.
          autoComplete={field.isPassword ? "new-password" : "off"}
          rightUnit={
            field.isPassword ? (
              <KuikaSvg
                name={isRevealed ? "eye" : "eye-closed"}
                pointer
                onClick={() => setRevealedField(isRevealed ? null : field.name)}
              />
            ) : undefined
          }
        />
      </KuikaFormField>
    );
  };

  /**
   * Which generated types this auth type offers, and which are already taken. A type stays
   * listed on the row that holds it so the row can still show what it is.
   */
  const typeOptions = (rowIndex: number): KuikaSelectboxOption[] => {
    const options: KuikaSelectboxOption[] = [
      { value: PARAMETER_TYPE_CUSTOM, label: TYPE_LABELS[PARAMETER_TYPE_CUSTOM] }
    ];

    if (authType === EAuthenticationType.ClientIdSecret || authType === EAuthenticationType.KuikaAuth) {
      options.push({
        value: PARAMETER_TYPE_USERNAME,
        label: TYPE_LABELS[PARAMETER_TYPE_USERNAME],
        disabled: userNameHeaderParameterIndex > -1 && userNameHeaderParameterIndex !== rowIndex
      });
    }
    if (authType === EAuthenticationType.Basic) {
      options.push({
        value: PARAMETER_TYPE_BASIC_AUTH,
        label: TYPE_LABELS[PARAMETER_TYPE_BASIC_AUTH],
        disabled: basicAuthHeaderParameterIndex > -1 && basicAuthHeaderParameterIndex !== rowIndex
      });
    }
    if (authType === EAuthenticationType.ClientIdSecret) {
      options.push({
        value: PARAMETER_TYPE_AUTH_TOKEN,
        label: TYPE_LABELS[PARAMETER_TYPE_AUTH_TOKEN],
        disabled: authTokenHeaderParameterIndex > -1 && authTokenHeaderParameterIndex !== rowIndex
      });
    }

    return options;
  };

  return (
    <>
      {authType === EAuthenticationType.ClientIdSecret && CLIENT_FIELDS.map(renderCredentialField)}
      {authType === EAuthenticationType.Basic && BASIC_FIELDS.map(renderCredentialField)}

      <div className="kuika-form-section__wide">
        <KuikaEditGridFormList<IApiKeyParameter>
          name={HEADER_ROWS_PATH}
          isBlank={isBlankHeaderParameter}
          makeBlank={blankHeaderParameter}
        >
          {(rows: IEditGridListRow[], { settle, remove }: IEditGridListApi): React.ReactNode => (
            <KuikaEditGrid columns={HEADER_COLUMNS} label="Headers" testId={`${testIdPrefix}HeadersGrid`}>
              {rows.map((row: IEditGridListRow): JSX.Element => {
                const isPinnedBasicAuthRow: boolean = basicAuthHeaderParameterIndex === row.index;
                const holdsGeneratedValue: boolean = isGeneratedHeaderRow(generatedRows, row.index);
                const next: IEditGridListRow | undefined = rows[row.index + 1];

                return (
                  <KuikaEditGridRow
                    key={row.key}
                    locked={holdsGeneratedValue}
                    testId={row.isDraft ? `${testIdPrefix}HeaderDraftRow` : undefined}
                  >
                    <KuikaEditGridCell flush>
                      <KuikaFormField name={[row.name, "type"]}>
                        <KuikaSelectbox
                          size="md"
                          className="kuika-edit-grid__select"
                          options={typeOptions(row.index)}
                          disabled={authType === EAuthenticationType.Basic && isPinnedBasicAuthRow}
                          data-testid={row.isDraft ? `${testIdPrefix}HeaderDraftTypeSelect` : undefined}
                          onChange={settle}
                        />
                      </KuikaFormField>
                    </KuikaEditGridCell>
                    <KuikaEditGridCell flush>
                      <KuikaFormField name={[row.name, "key"]}>
                        <KuikaInput
                          size="md"
                          borderless
                          placeholder="Enter key.."
                          autoComplete="off"
                          disabled={isPinnedBasicAuthRow}
                          data-testid={row.isDraft ? `${testIdPrefix}HeaderDraftKeyInput` : undefined}
                          data-edit-grid-nav={editGridNavId(row.key, "key")}
                          onChange={settle}
                          onPressEnter={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                            event.preventDefault();
                            // A generated value cannot take the caret, so the walk skips to the next row.
                            const target: IEditGridListRow | undefined = holdsGeneratedValue ? next : row;
                            if (target) {
                              focusEditGridCell(
                                event.currentTarget,
                                editGridNavId(target.key, holdsGeneratedValue ? "key" : "value")
                              );
                            }
                          }}
                        />
                      </KuikaFormField>
                    </KuikaEditGridCell>
                    {/* A generated value is locked, and says why: the glyph's tooltip names the
                        auth type that writes it, which a greyed-out field alone never did. */}
                    <KuikaEditGridCell flush generated={holdsGeneratedValue}>
                      <KuikaFormField name={[row.name, "value"]}>
                        <KuikaInput
                          size="md"
                          borderless
                          placeholder="Enter value.."
                          autoComplete="off"
                          disabled={holdsGeneratedValue}
                          data-testid={row.isDraft ? `${testIdPrefix}HeaderDraftValueInput` : undefined}
                          data-edit-grid-nav={editGridNavId(row.key, "value")}
                          onChange={settle}
                          onPressEnter={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                            event.preventDefault();
                            if (next) focusEditGridCell(event.currentTarget, editGridNavId(next.key, "key"));
                          }}
                          rightUnit={
                            holdsGeneratedValue ? (
                              <KuikaTooltip content={lockedValueTooltip} position="top">
                                <span className="kuika-edit-grid__lock" role="img" aria-label={lockedValueTooltip}>
                                  <KuikaSvg name="lock" width={14} height={14} />
                                </span>
                              </KuikaTooltip>
                            ) : undefined
                          }
                        />
                      </KuikaFormField>
                    </KuikaEditGridCell>
                    {/* Every row but the pinned Basic one and the draft can go. The group this
                        replaced drew the control only while the auth type was Basic, so under No
                        Auth, Bearer or API Key a header could be added and then never removed. */}
                    <KuikaEditGridCell center>
                      {!row.isDraft && !isPinnedBasicAuthRow && (
                        <KuikaEditGridRemove label="Remove header" onClick={(): void => remove(row.index)} />
                      )}
                    </KuikaEditGridCell>
                  </KuikaEditGridRow>
                );
              })}
            </KuikaEditGrid>
          )}
        </KuikaEditGridFormList>
      </div>
    </>
  );
};
